// src/components/CaseForm/index.jsx
// Shared building blocks for the Walk-In and Pledge Release case forms (the
// part below the item table): titled section cards, a customer picker with a
// preview card, and an agent picker. Field values still flow through antd
// Form.Item — the pickers take the injected value/onChange like any input.
import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Select, Tag, Typography } from 'antd';
import {
  UserOutlined, PhoneOutlined, IdcardOutlined, SearchOutlined,
  SafetyCertificateOutlined, TeamOutlined,
} from '@ant-design/icons';
import './CaseForm.css';

const { Text } = Typography;

const initialsOf = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

/** Titled card grouping related fields. */
export function FormSection({ title, icon, description, extra, children }) {
  return (
    <section className="cf-section">
      <header className="cf-section-head">
        <div className="cf-section-heading">
          {icon && <span className="cf-section-icon">{icon}</span>}
          <div>
            <div className="cf-section-title">{title}</div>
            {description && <div className="cf-section-desc">{description}</div>}
          </div>
        </div>
        {extra && <div className="cf-section-extra">{extra}</div>}
      </header>
      <div className="cf-section-body">{children}</div>
    </section>
  );
}

const KYC_TAG = {
  verified: { color: 'green', text: 'KYC Verified' },
  pending: { color: 'gold', text: 'KYC Pending' },
  failed: { color: 'red', text: 'KYC Failed' },
};

function CustomerOption({ c }) {
  return (
    <div className="cf-option">
      <Avatar size={34} src={c.photo || undefined} className="cf-avatar">
        {initialsOf(c.name)}
      </Avatar>
      <div className="cf-option-main">
        <div className="cf-option-title">{c.name}</div>
        <div className="cf-option-meta">
          <span><PhoneOutlined /> {c.phone || '—'}</span>
          {c.customerId && <span><IdcardOutlined /> {c.customerId}</span>}
        </div>
      </div>
      {KYC_TAG[c.kycStatus] && (
        <Tag color={KYC_TAG[c.kycStatus].color} className="cf-option-tag">{KYC_TAG[c.kycStatus].text}</Tag>
      )}
    </div>
  );
}

/**
 * Searchable customer picker. `customers` is the current search result list;
 * `onSearch` receives the (debounced) query. `selectedCustomer` is the fully
 * resolved record and drives the preview card under the field.
 */
export function CustomerSelect({ value, onChange, customers = [], onSearch, selectedCustomer, disabled }) {
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  // The parent swaps the list once its fetch resolves — that ends the search.
  useEffect(() => setSearching(false), [customers]);

  const handleSearch = (q) => {
    clearTimeout(timer.current);
    setSearching(true);
    timer.current = setTimeout(() => onSearch?.(q), 300);
  };

  // The current customer may sit outside the search window (edits, or a
  // search that has since moved on) — keep it in the list so the field
  // shows a name instead of a raw id.
  const list = selectedCustomer && value && !customers.some((c) => c._id === value)
    ? [selectedCustomer, ...customers]
    : customers;

  return (
    <>
      <Select
        showSearch
        size="large"
        value={value}
        onChange={onChange}
        onSearch={handleSearch}
        filterOption={false}
        loading={searching}
        disabled={disabled}
        placeholder="Search by name, phone or customer ID"
        suffixIcon={<SearchOutlined />}
        notFoundContent={searching ? 'Searching…' : 'No customers found'}
        popupClassName="cf-select-popup"
        options={list.map((c) => ({ value: c._id, label: c.name, customer: c }))}
        optionRender={(opt) => <CustomerOption c={opt.data.customer} />}
        labelRender={({ value: v }) => {
          const c = list.find((x) => x._id === v);
          return c ? <span>{c.name} <Text type="secondary">· {c.phone}</Text></span> : v;
        }}
      />
      {selectedCustomer && (
        <div className="cf-preview">
          <Avatar size={44} src={selectedCustomer.photo || undefined} className="cf-avatar">
            {initialsOf(selectedCustomer.name)}
          </Avatar>
          <div className="cf-preview-main">
            <div className="cf-preview-name">
              {selectedCustomer.name}
              {KYC_TAG[selectedCustomer.kycStatus] && (
                <Tag
                  color={KYC_TAG[selectedCustomer.kycStatus].color}
                  icon={<SafetyCertificateOutlined />}
                  className="cf-option-tag"
                >
                  {KYC_TAG[selectedCustomer.kycStatus].text}
                </Tag>
              )}
            </div>
            <div className="cf-preview-grid">
              <span><PhoneOutlined /> {selectedCustomer.phone || '—'}</span>
              {selectedCustomer.customerId && <span><IdcardOutlined /> {selectedCustomer.customerId}</span>}
              {selectedCustomer.aadhaar && <span>Aadhaar · {selectedCustomer.aadhaar}</span>}
              {selectedCustomer.pan && <span>PAN · {selectedCustomer.pan}</span>}
            </div>
            {selectedCustomer.address && <div className="cf-preview-address">{selectedCustomer.address}</div>}
          </div>
        </div>
      )}
    </>
  );
}

/** Agent picker. `onAgentChange(agent | null)` fires alongside the form onChange. */
export function AgentSelect({ value, onChange, agents = [], onAgentChange, disabled }) {
  const handleChange = (v) => {
    onChange?.(v);
    onAgentChange?.(agents.find((a) => a._id === v) || null);
  };
  return (
    <Select
      showSearch
      allowClear
      size="large"
      value={value}
      onChange={handleChange}
      disabled={disabled}
      placeholder="No agent assigned"
      suffixIcon={<TeamOutlined />}
      popupClassName="cf-select-popup"
      filterOption={(input, opt) =>
        `${opt.data.agent.name} ${opt.data.agent.agentId} ${opt.data.agent.phone || ''}`
          .toLowerCase().includes(input.toLowerCase())}
      notFoundContent="No active agents"
      options={agents.map((a) => ({ value: a._id, label: a.name, agent: a }))}
      optionRender={(opt) => {
        const a = opt.data.agent;
        return (
          <div className="cf-option">
            <Avatar size={30} className="cf-avatar cf-avatar-agent" icon={<UserOutlined />} />
            <div className="cf-option-main">
              <div className="cf-option-title">{a.name}</div>
              <div className="cf-option-meta">
                <span>{a.agentId}</span>
                {a.phone && <span><PhoneOutlined /> {a.phone}</span>}
              </div>
            </div>
            <Tag color="blue" className="cf-option-tag">{a.defaultCommissionPercent ?? 0}%</Tag>
          </div>
        );
      }}
      labelRender={({ value: v }) => {
        const a = agents.find((x) => x._id === v);
        return a ? <span>{a.name} <Text type="secondary">· {a.agentId}</Text></span> : v;
      }}
    />
  );
}
